import React, { useState } from 'react';
import { 
  Gift, MessageCircle, Mail, Smartphone, Share2, 
  CheckCircle, Lock, Unlock, Clock, ArrowRight
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import './ShareGyyfts.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const APP_LINK = "https://play.google.com/store/apps/details?id=com.customers.bhyyve";
const POLLEN_IMG = "https://customer-assets.emergentagent.com/job_bee-booking/artifacts/k8zgjyqb_Pollen%20-%20bHyyve%20Mascot.png";

// Generate a unique coupon code
const generateCouponCode = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let code = 'IPTV15-';
  for (let i = 0; i < 6; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
};

const ShareGyyfts = () => {
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [shareMethod, setShareMethod] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Email form state
  const [emailForm, setEmailForm] = useState({
    senderFirstName: '',
    senderLastName: '',
    senderEmail: '',
    recipientFirstName: '',
    recipientLastName: '',
    recipientEmail: ''
  });

  const handleEmailInputChange = (field, value) => {
    setEmailForm(prev => ({ ...prev, [field]: value }));
  };

  const notifyBackend = async (method, details = {}) => {
    try {
      const response = await fetch(`${API_URL}/api/share-gyyft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shareMethod: method,
          couponCode: couponCode || generateCouponCode(),
          ...details
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to record share');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Backend notification error:', error);
      throw error;
    }
  };

  const handleTextShare = async () => {
    const code = generateCouponCode();
    setCouponCode(code);
    
    const message = `Hey! Check out the bHyyve app and get great deals on services! Download here: ${APP_LINK}`;
    
    // Try to use native share on mobile, fallback to SMS link
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'bHyyve App',
          text: message,
          url: APP_LINK
        });
        await notifyBackend('text', { couponCode: code });
        setIsUnlocked(true);
        toast.success('Shared successfully! Your coupon is unlocked!');
      } catch (err) {
        if (err.name !== 'AbortError') {
          // Fallback to SMS
          window.open(`sms:?body=${encodeURIComponent(message)}`, '_blank');
          await notifyBackend('text', { couponCode: code });
          setIsUnlocked(true);
          toast.success('Coupon unlocked!');
        }
      }
    } else {
      window.open(`sms:?body=${encodeURIComponent(message)}`, '_blank');
      try {
        await notifyBackend('text', { couponCode: code });
      } catch (e) {}
      setIsUnlocked(true);
      setCouponCode(code);
      toast.success('Coupon unlocked!');
    }
  };

  const handleWhatsAppShare = async () => {
    const code = generateCouponCode();
    setCouponCode(code);
    
    const message = `Hey! Check out the bHyyve app and get great deals on services! Download here: ${APP_LINK}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    
    window.open(whatsappUrl, '_blank');
    
    try {
      await notifyBackend('whatsapp', { couponCode: code });
    } catch (e) {}
    
    setIsUnlocked(true);
    toast.success('Coupon unlocked!');
  };

  const handleEmailShare = async (e) => {
    e.preventDefault();
    
    // Validate all fields
    const { senderFirstName, senderLastName, senderEmail, recipientFirstName, recipientLastName, recipientEmail } = emailForm;
    
    if (!senderFirstName || !senderLastName || !senderEmail || !recipientFirstName || !recipientLastName || !recipientEmail) {
      toast.error('Please fill in all fields');
      return;
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(senderEmail) || !emailRegex.test(recipientEmail)) {
      toast.error('Please enter valid email addresses');
      return;
    }
    
    setIsSubmitting(true);
    const code = generateCouponCode();
    
    try {
      await notifyBackend('email', {
        couponCode: code,
        sender: {
          firstName: senderFirstName,
          lastName: senderLastName,
          email: senderEmail
        },
        recipient: {
          firstName: recipientFirstName,
          lastName: recipientLastName,
          email: recipientEmail
        }
      });
      
      setCouponCode(code);
      setIsUnlocked(true);
      setShareMethod(null);
      toast.success('Email sent! Your coupon is unlocked!');
    } catch (error) {
      toast.error('Failed to send. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(couponCode);
    toast.success('Coupon code copied!');
  };

  return (
    <div className="sg-page" data-testid="share-gyyfts-page">
      {/* Header */}
      <header className="sg-header">
        <a href="/" className="sg-logo-link">
          <img src="/logo.svg" alt="bHyyve" className="sg-logo" />
          <span className="sg-logo-text">bHyyve</span>
        </a>
      </header>

      <div className="sg-content">
        {/* Left Side - Offer */}
        <div className="sg-offer-side">
          <div className="sg-offer-card">
            <div className="sg-offer-badge">
              <Gift size={18} />
              <span>Exclusive Gyyft</span>
            </div>
            
            <h1 className="sg-offer-title">
              <span className="sg-discount">15% OFF</span>
              <span className="sg-product">Android IPTV Box</span>
            </h1>
            
            <p className="sg-offer-desc">
              Share the bHyyve app with a friend or family member to unlock your exclusive discount code!
            </p>

            {/* Coupon Display */}
            <div className={`sg-coupon-box ${isUnlocked ? 'unlocked' : 'locked'}`}>
              <div className="sg-coupon-label">
                {isUnlocked ? <Unlock size={18} /> : <Lock size={18} />}
                <span>{isUnlocked ? 'Your Coupon Code' : 'Locked Coupon'}</span>
              </div>
              <div className="sg-coupon-code-wrapper">
                <span className={`sg-coupon-code ${!isUnlocked ? 'blurred' : ''}`}>
                  {isUnlocked ? couponCode : 'IPTV15-XXXXXX'}
                </span>
                {isUnlocked && (
                  <Button 
                    className="sg-copy-btn"
                    onClick={copyToClipboard}
                    data-testid="copy-coupon-btn"
                  >
                    Copy
                  </Button>
                )}
              </div>
              {isUnlocked && (
                <div className="sg-validity">
                  <Clock size={14} />
                  <span>Valid for 15 days</span>
                </div>
              )}
            </div>

            {!isUnlocked && (
              <p className="sg-unlock-hint">
                <Share2 size={16} />
                Share using any method below to unlock
              </p>
            )}

            {isUnlocked && (
              <div className="sg-success-message">
                <CheckCircle size={20} />
                <span>Use this code in your bHyyve mobile app at checkout!</span>
              </div>
            )}
          </div>

          {/* Pollen Mascot */}
          <div className="sg-pollen-container">
            <img src={POLLEN_IMG} alt="Pollen - bHyyve Mascot" className="sg-pollen" />
            <div className="sg-pollen-bubble">
              {isUnlocked 
                ? "Yay! You unlocked your discount! Enjoy! 🎉" 
                : "Share with a friend to unlock your special Gyyft! 🐝"}
            </div>
          </div>
        </div>

        {/* Right Side - Share Options */}
        <div className="sg-share-side">
          {shareMethod !== 'email' ? (
            <>
              <h2 className="sg-share-title">Choose How to Share</h2>
              
              <div className="sg-share-options">
                <button 
                  className="sg-share-btn sg-text-btn"
                  onClick={handleTextShare}
                  disabled={isUnlocked}
                  data-testid="share-text-btn"
                >
                  <div className="sg-share-icon">
                    <Smartphone size={28} />
                  </div>
                  <span className="sg-share-label">Text Message</span>
                  <span className="sg-share-desc">Send via SMS</span>
                </button>

                <button 
                  className="sg-share-btn sg-whatsapp-btn"
                  onClick={handleWhatsAppShare}
                  disabled={isUnlocked}
                  data-testid="share-whatsapp-btn"
                >
                  <div className="sg-share-icon">
                    <MessageCircle size={28} />
                  </div>
                  <span className="sg-share-label">WhatsApp</span>
                  <span className="sg-share-desc">Share instantly</span>
                </button>

                <button 
                  className="sg-share-btn sg-email-btn"
                  onClick={() => setShareMethod('email')}
                  disabled={isUnlocked}
                  data-testid="share-email-btn"
                >
                  <div className="sg-share-icon">
                    <Mail size={28} />
                  </div>
                  <span className="sg-share-label">Email</span>
                  <span className="sg-share-desc">Send an invite</span>
                </button>
              </div>

              {isUnlocked && (
                <div className="sg-download-prompt">
                  <p>Don't have the app yet?</p>
                  <a 
                    href={APP_LINK} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="sg-download-btn"
                    data-testid="download-app-btn"
                  >
                    Download bHyyve App
                    <ArrowRight size={18} />
                  </a>
                </div>
              )}
            </>
          ) : (
            <div className="sg-email-form-container">
              <button 
                className="sg-back-btn"
                onClick={() => setShareMethod(null)}
                data-testid="back-btn"
              >
                ← Back to share options
              </button>
              
              <h2 className="sg-share-title">Share via Email</h2>
              
              <form onSubmit={handleEmailShare} className="sg-email-form" data-testid="email-share-form">
                <div className="sg-form-section">
                  <h3>Your Information</h3>
                  <div className="sg-form-row">
                    <div className="sg-field">
                      <label>First Name</label>
                      <Input
                        type="text"
                        value={emailForm.senderFirstName}
                        onChange={(e) => handleEmailInputChange('senderFirstName', e.target.value)}
                        placeholder="Your first name"
                        required
                        data-testid="sender-first-name"
                      />
                    </div>
                    <div className="sg-field">
                      <label>Last Name</label>
                      <Input
                        type="text"
                        value={emailForm.senderLastName}
                        onChange={(e) => handleEmailInputChange('senderLastName', e.target.value)}
                        placeholder="Your last name"
                        required
                        data-testid="sender-last-name"
                      />
                    </div>
                  </div>
                  <div className="sg-field">
                    <label>Your Email</label>
                    <Input
                      type="email"
                      value={emailForm.senderEmail}
                      onChange={(e) => handleEmailInputChange('senderEmail', e.target.value)}
                      placeholder="you@example.com"
                      required
                      data-testid="sender-email"
                    />
                  </div>
                </div>

                <div className="sg-form-section">
                  <h3>Recipient Information</h3>
                  <div className="sg-form-row">
                    <div className="sg-field">
                      <label>First Name</label>
                      <Input
                        type="text"
                        value={emailForm.recipientFirstName}
                        onChange={(e) => handleEmailInputChange('recipientFirstName', e.target.value)}
                        placeholder="Their first name"
                        required
                        data-testid="recipient-first-name"
                      />
                    </div>
                    <div className="sg-field">
                      <label>Last Name</label>
                      <Input
                        type="text"
                        value={emailForm.recipientLastName}
                        onChange={(e) => handleEmailInputChange('recipientLastName', e.target.value)}
                        placeholder="Their last name"
                        required
                        data-testid="recipient-last-name"
                      />
                    </div>
                  </div>
                  <div className="sg-field">
                    <label>Recipient Email</label>
                    <Input
                      type="email"
                      value={emailForm.recipientEmail}
                      onChange={(e) => handleEmailInputChange('recipientEmail', e.target.value)}
                      placeholder="friend@example.com"
                      required
                      data-testid="recipient-email"
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="sg-submit-btn"
                  disabled={isSubmitting}
                  data-testid="send-email-btn"
                >
                  {isSubmitting ? 'Sending...' : (
                    <>
                      <Mail size={18} />
                      Send & Unlock Coupon
                    </>
                  )}
                </Button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareGyyfts;
